import { skipToken } from "@reduxjs/toolkit/query";
import axios from "axios";
import moment from "moment";
import { useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { useParams } from "react-router-dom";

import { AUTH_ROLES } from "api/common/types/auth.types";
import { useGetProductFeedbackQuery } from "api/memberPortal/product/feedback/feedback.api";
import { PRODUCT_STATUSES } from "api/memberPortal/project/product/productStatuses.type";
import DeleteModal from "components/UI/DeleteModal";
import DueDateBanner from "features/memberPortal/products/productDetails/ProficiencyFeedback/components/DueDateBanner";
import HeaderProficiencyFeedbackButtons from "features/memberPortal/products/productDetails/ProficiencyFeedback/components/HeaderProficiencyFeedbackButtons";
import { useAuth } from "hooks/useAuth";

export default function WithdrawalTerminationFeedbackHeader({
  product,
  setAlertInfo,
  refetch,
}) {
  const { authToken, csrfToken, currentUser, permissions } = useAuth();

  const [requestWithdrawalInitiated, setRequestWithdrawalInitiated] =
    useState(false);
  const [niapReviewWithdrawal, setNiapReviewWithdrawal] = useState("");

  const [requestWithdrawalCompleted, setRequestWithdrawalCompleted] =
    useState(false);
  const [hideWithdrawalRequest, setHideWithdrawalRequest] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { id } = useParams();

  const { data: feedbackForm } = useGetProductFeedbackQuery(
    id
      ? {
          parentId: id,
        }
      : skipToken,
  );

  const handleCreateAlerts = async (groups, text) => {
    //sending alert to labs, vendors, and NIAP
    for (let group in groups) {
      let data = {
        alert_on: new Date(
          new Date().setMinutes(
            new Date().getMinutes() - new Date().getTimezoneOffset(),
          ),
        ),
        alert_type: "Product",
        alert_text: text,
        alert_type_id: product?.product_id,
        active: true,
        alert_to: null,
        alert_source: "Withdrawal Request",
        alert_to_vt: null,
        alert_to_group: null,
      };
      if (groups[group] === "NIAP") {
        data["alert_to_role"] = "NIAP";
      } else {
        data["alert_to_group"] = groups[group];
      }
      await axios
        .post(`${import.meta.env.VITE_DJANGO_ENDPOINT}alerts/`, data, {
          withCredentials: true,
          headers: {
            "X-CSRFToken": csrfToken,
            Authorization: `Token ${authToken}`,
          },
        })
        .then(() => {})
        .catch((error) =>
          console.log("Unable to create alert for Withdrawal Request.", error),
        );
    }
  };

  const handleWithdrawalRequest = (e, by, type) => {
    let data = {};
    if (by === "NIAP") {
      if (type === "Accept") {
        data = {
          status: "Withdrawn",
          withdrawal_date: new Date(
            new Date().setMinutes(
              new Date().getMinutes() - new Date().getTimezoneOffset(),
            ),
          ),
          request_withdrawal: false,
        };
      } else {
        data = {
          request_withdrawal: false,
          request_withdrawal_date: null,
          request_withdrawal_user: null,
        };
      }
    } else {
      data = {
        request_withdrawal: true,
        request_withdrawal_date: new Date(
          new Date().setMinutes(
            new Date().getMinutes() - new Date().getTimezoneOffset(),
          ),
        ),
        request_withdrawal_user: currentUser?.id,
      };
    }

    axios
      .put(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}project/product/${product?.product_id}/`,
        data,
        {
          withCredentials: true,
          headers: {
            "X-CSRFToken": csrfToken,
            Authorization: `Token ${authToken}`,
          },
        },
      )
      .then(() => {
        //sending alert to labs, vendors, and NIAP
        if (type === "Accept") {
          // handleAlertAndMailer("Withdrawal") //is sending success email and alert
          setAlertInfo({
            variant: "success",
            message: `Product VID${product.v_id} successfully withdrawn.`,
          });
        } else if (data.request_withdrawal === true) {
          setAlertInfo({
            variant: "success",
            message: `Product VID${product.v_id} withdrawal successfully requested.`,
          });
        } else {
          setAlertInfo({
            variant: "success",
            message: `Product VID${product.v_id} withdrawal successfully denied.`,
          });
        }
        setRequestWithdrawalInitiated(false);
        setRequestWithdrawalCompleted(true);

        let message = "";
        if (by === "NIAP") {
          if (type !== "Accept") {
            message = `VID ${product?.v_id} ${product?.product_name} has been denied for withdrawal.`;
          }
        } else {
          message = `VID ${product?.v_id} ${product?.product_name} has been requested for withdrawal.`;
        }
        handleCreateAlerts(
          [product?.vendor_id?.org_id, product?.assigned_lab?.org_id, "NIAP"],
          message,
        );
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const updateProduct = () => {
    let data = {};

    if (showDeleteModal) {
      data.status = "Terminated";
      data.publish_status = "Unpublished";
    } else {
      data.record_closeout_date = new Date();
    }
    axios
      .put(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}project/product/${product?.product_id}/`,
        data,
        {
          withCredentials: true,
          headers: {
            "X-CSRFToken": csrfToken,
            Authorization: `Token ${authToken}`,
          },
        },
      )
      .then(() => {
        refetch();
        if (showDeleteModal) {
          setShowDeleteModal(false);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const withdrawalButtons = () => {
    if (product?.request_withdrawal === true) {
      return (
        <Row>
          <Col className="px-0" sm={12} md={4}>
            {niapReviewWithdrawal === "" ? (
              !hideWithdrawalRequest && (
                <h5 className="fw-bold text-danger">
                  Withdrawal Requested by{" "}
                  {product?.request_withdrawal_user?.first_name}{" "}
                  {product?.request_withdrawal_user?.last_name} on{" "}
                  {moment
                    .utc(product?.request_withdrawal_date)
                    .format("MM/DD/YYYY")}
                </h5>
              )
            ) : (
              <h5>
                {niapReviewWithdrawal} Withdrawal Request by{" "}
                {product?.request_withdrawal_user?.first_name}{" "}
                {product?.request_withdrawal_user?.last_name} on{" "}
                {moment
                  .utc(product?.request_withdrawal_date)
                  .format("MM/DD/YYYY")}
                ?
              </h5>
            )}
          </Col>
          {permissions?.role_type === "NIAP" &&
            !hideWithdrawalRequest &&
            niapReviewWithdrawal === "" && (
              <Col sm={12} md={4} className="d-flex align-items-center">
                <Button
                  variant="danger"
                  className="rounded-pill me-2"
                  onClick={() => {
                    setNiapReviewWithdrawal("Reject");
                    setHideWithdrawalRequest(true);
                  }}
                >
                  Reject Withdrawal
                </Button>
                <Button
                  variant="success"
                  className="rounded-pill"
                  onClick={() => {
                    setNiapReviewWithdrawal("Accept");
                    setHideWithdrawalRequest(true);
                  }}
                >
                  Accept Withdrawal
                </Button>
              </Col>
            )}
          {permissions?.role_type === "NIAP" && niapReviewWithdrawal !== "" && (
            <>
              <Col sm={12} md={2} className="mt-1 mb-4">
                <Button
                  variant="warning"
                  className="rounded-pill"
                  onClick={() => {
                    setNiapReviewWithdrawal("");
                    setHideWithdrawalRequest(false);
                  }}
                >
                  Cancel
                </Button>
              </Col>
              <Col sm={12} md={2} className="mt-1 mb-4">
                <Button
                  variant={
                    niapReviewWithdrawal === "Reject" ? "danger" : "success"
                  }
                  className="rounded-pill"
                  onClick={(e) => {
                    handleWithdrawalRequest(e, "NIAP", niapReviewWithdrawal);
                    setNiapReviewWithdrawal("");
                  }}
                >
                  {niapReviewWithdrawal}
                </Button>
              </Col>
            </>
          )}
        </Row>
      );
    } else if (
      requestWithdrawalInitiated &&
      permissions?.role_type !== "NIAP" &&
      permissions?.role_type !== "Validator"
    ) {
      return (
        <Col className="px-0">
          <p>Are you sure you want to Request Withdrawal?</p>
          <Button
            variant="warning"
            className="me-3 mb-2 rounded-pill"
            onClick={() => setRequestWithdrawalInitiated(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            className="me-3 mb-2 rounded-pill"
            onClick={handleWithdrawalRequest}
          >
            Request Withdrawal
          </Button>
        </Col>
      );
    } else if (
      product?.certification_status !== "Certified" &&
      product?.status !== "Withdrawn" &&
      permissions?.role_type !== "NIAP" &&
      permissions?.role_type !== "Validator" &&
      requestWithdrawalCompleted === false
    ) {
      return (
        <Col className="px-0">
          <Button
            variant="outline-danger"
            className="me-3 mb-2 rounded-pill"
            onClick={() => setRequestWithdrawalInitiated(true)}
          >
            Request Withdrawal
          </Button>
        </Col>
      );
    }
  };

  const isValidator = permissions?.role_type === AUTH_ROLES.VALIDATOR;
  const productNotLegacy =
    product?.posted_date && moment(product.posted_date).isAfter("2025-01-01");
  const productIsNotPosted = product?.status !== PRODUCT_STATUSES.CERTIFIED;
  const proficiencyFormNotSubmitted = feedbackForm?.status === "draft";

  return (
    <>
      <Row>
        <Col sm={8}>
          {feedbackForm && (
            <>
              <DueDateBanner
                product={product}
                sx={{ my: 2 }}
                show={
                  isValidator &&
                  productNotLegacy &&
                  productIsNotPosted &&
                  proficiencyFormNotSubmitted
                }
              />
              <HeaderProficiencyFeedbackButtons
                product={product}
                feedbackForm={feedbackForm}
                isDraft={proficiencyFormNotSubmitted}
                isSubmitted={!proficiencyFormNotSubmitted}
              />
            </>
          )}
          {withdrawalButtons()}
        </Col>
      </Row>
      <Row>
        <Col sm={4} className="mt-3 mb-0">
          {permissions?.role_type === "NIAP" &&
            ["In Progress", "Pre-Evaluation"].includes(product?.status) && (
              <Button
                variant="danger"
                className="me-3 mb-2 rounded-pill"
                onClick={() => setShowDeleteModal(true)}
              >
                Terminate
              </Button>
            )}
        </Col>
      </Row>
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        deleteFunction={updateProduct}
        type={"Product"}
        isTerminate={true}
      />
    </>
  );
}
